/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import rehypeSanitize from 'rehype-sanitize';

import Monster from '../model/Monster';
import Abilities from '../model/Abilities';

// MarkdownTextHelpers.ts
export function processText(sourceText: string): string[] {
  const sections: string[] = [];
  let currentSection: string[] = [];

  sourceText?.split("\n").forEach((line) => {
    const trimmedLine = line.trim();
    const isGrouping = trimmedLine.startsWith('|') || trimmedLine.startsWith('* ') || trimmedLine.startsWith('- ');

    if (isGrouping) {
      currentSection.push(line);
    } else {
      if (currentSection.length) {
        sections.push(currentSection.join('\n'));
        currentSection = [];
      }
      sections.push(line);
    }
  });

  if (currentSection.length) {
    sections.push(currentSection.join('\n'));
  }

  return sections;
}

/**
 * Removes dangerous inline HTML before markdown parsing.
 * This is intentionally conservative; rehype-sanitize
 * provides the real safety net.
 */
export function sanitizeMarkdownInput(input: string): string {
    if (!input) {
        return '';
    }

    return input
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
}

/**
 * Applies domain-specific tag replacement (e.g. ability tags)
 * before markdown parsing so the resulting text is part of
 * the markdown AST.
 */
export function replaceAbilityTags(text: string, mon: Monster): string {
    return Abilities.ReplaceTags(text, mon);
}

/**
 * Converts markdown text into an HTML string.
 * Intended for export (files, downloads, emails, etc.).
 */
export async function markdownToHtml(text: string, mon: Monster | null): Promise<string> {
    if (!text) {
        return '';
    }

    const sanitized = sanitizeMarkdownInput(text);
    const finalText = mon
        ? replaceAbilityTags(sanitized, mon)
        : sanitized;

    const file = await unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, {
            allowDangerousHtml: false
        })
        .use(rehypeSanitize)
        .use(rehypeStringify)
        .process(finalText);

    return String(file);
}
